import React from 'react';
import { Badge, Card, Group, Paper, Stack, Table, Text } from '@mantine/core';
import { Calendar, type DayOfWeek } from '@mantine/dates';
import { dateFormater } from '../helpers/common';
import { CutoffSpec, Fund, OrderStatusCode, SubscriptionType } from '../types';
import { getNavStartAndEndDateOfWeek } from '../../../shared/geneva-functions';
import { fromMantineDate } from '../helpers/mantineDates';

type MonitoringStatus = OrderStatusCode.waiting_for_nav | OrderStatusCode.processing;
const ORDER_TYPES: SubscriptionType[] = ["subscription", "redemption"];

export type OrderWindow = { start: Date; end: Date };
type OrderWindows = Partial<Record<SubscriptionType, OrderWindow>>;
type OrderWindowSummary = Record<MonitoringStatus, OrderWindows>;

export interface OrderWindowsCalendarProps {
    fund: Fund;
    waitingCount?: number;
    processingCount?: number;
    weekendDays?: DayOfWeek[];
}

const toStartOfDay = (value: Date) => {
    const day = new Date(value);
    day.setHours(0, 0, 0, 0);
    return day;
};

const isDateInWindow = (date: Date, window?: OrderWindow) => {
    if (!window) {
        return false;
    }
    const day = toStartOfDay(date).getTime();
    const start = toStartOfDay(window.start).getTime();
    const end = toStartOfDay(window.end).getTime();
    return day >= start && day <= end;
};

const getEarliestWindowStart = (windows: OrderWindows) => {
    const configuredWindows = ORDER_TYPES
        .map((type) => windows[type])
        .filter((window): window is OrderWindow => Boolean(window));
    if (!configuredWindows.length) {
        return null;
    }
    return configuredWindows.reduce(
        (earliest, window) => (window.start < earliest ? window.start : earliest),
        configuredWindows[0].start,
    );
};

const alignDateToNavDay = (seed: Date, navUpdateDay: number): Date => {
    const aligned = new Date(seed);
    aligned.setHours(0, 0, 0, 0);
    const diff = (navUpdateDay - aligned.getDay() + 7) % 7;
    aligned.setDate(aligned.getDate() + diff);
    return aligned;
};

const resolveNavDateForStatus = (
    status: MonitoringStatus,
    fundNavDate: Date,
    navUpdateDay: number
): Date => {
    if (status === OrderStatusCode.processing) {
        return new Date(fundNavDate);
    }
    const nextBasedOnFund = new Date(fundNavDate);
    nextBasedOnFund.setDate(nextBasedOnFund.getDate() + 7);
    let candidate = alignDateToNavDay(new Date(), navUpdateDay);
    if (candidate <= nextBasedOnFund) {
        candidate = nextBasedOnFund;
    }
    return candidate;
};

const getExpectedNavDateForNow = (
    now: Date,
    cutoff: CutoffSpec,
    navUpdateDayOfWeek: number
): Date => {
    const candidateNavDay = new Date(now);
    candidateNavDay.setHours(3, 0, 0, 0);
    const currentDayOfWeek = candidateNavDay.getDay();
    const daysForward = (navUpdateDayOfWeek - currentDayOfWeek + 7) % 7;
    candidateNavDay.setDate(candidateNavDay.getDate() + daysForward);

    let { endOfWeek } = getNavStartAndEndDateOfWeek(candidateNavDay, cutoff, navUpdateDayOfWeek);
    if (now > endOfWeek) {
        candidateNavDay.setDate(candidateNavDay.getDate() + 7);
        ({ endOfWeek } = getNavStartAndEndDateOfWeek(candidateNavDay, cutoff, navUpdateDayOfWeek));
    }
    return candidateNavDay;
};

const createEmptySummary = (): OrderWindowSummary => ({
    waiting_for_nav: {},
    processing: {},
});

const OrderWindowsCalendar: React.FC<OrderWindowsCalendarProps> = ({
    fund,
    waitingCount = 0,
    processingCount = 0,
    weekendDays = [5, 6],
}) => {
    const navDate = fund.nav_date ? fund.nav_date.toDate() : null;
    const navUpdateDay = typeof fund.nav_update_day_of_week === "number" ? fund.nav_update_day_of_week : null;

    const orderWindowSummary = React.useMemo(() => {
        const summary = createEmptySummary();
        if (!navDate || navUpdateDay === null) {
            return summary;
        }

        const statuses: MonitoringStatus[] = [OrderStatusCode.waiting_for_nav, OrderStatusCode.processing];
        for (const status of statuses) {
            for (const type of ORDER_TYPES) {
                const cutoff = type === "subscription" ? fund.subscription_cutoff : fund.redemption_cutoff;
                if (!cutoff) {
                    continue;
                }
                const navDateForStatus = resolveNavDateForStatus(status, navDate, navUpdateDay);
                const { startOfWeek, endOfWeek } = getNavStartAndEndDateOfWeek(navDateForStatus, cutoff, navUpdateDay);
                summary[status][type] = {
                    start: new Date(startOfWeek),
                    end: new Date(endOfWeek),
                };
            }
        }

        return summary;
    }, [
        navDate ? navDate.getTime() : null,
        navUpdateDay,
        fund.subscription_cutoff?.daysBefore,
        fund.subscription_cutoff?.hour,
        fund.subscription_cutoff?.minute,
        fund.redemption_cutoff?.daysBefore,
        fund.redemption_cutoff?.hour,
        fund.redemption_cutoff?.minute,
    ]);

    const waitingWindows = orderWindowSummary[OrderStatusCode.waiting_for_nav];
    const processingWindows = orderWindowSummary[OrderStatusCode.processing];
    const waitingWindowStart = getEarliestWindowStart(waitingWindows);
    const processingWindowStart = getEarliestWindowStart(processingWindows);
    const navDateDayMillis = navDate ? toStartOfDay(navDate).getTime() : null;
    const nextNavDateDayMillis = React.useMemo(() => {
        if (!navDate) {
            return null;
        }
        const nextNav = toStartOfDay(navDate);
        nextNav.setDate(nextNav.getDate() + 7);
        return nextNav.getTime();
    }, [navDate ? navDate.getTime() : null]);

    const expectedNow = React.useMemo(() => {
        const expected: Partial<Record<SubscriptionType, Date>> = {};
        if (navUpdateDay === null) {
            return expected;
        }
        const now = new Date();
        if (fund.subscription_cutoff) {
            expected.subscription = getExpectedNavDateForNow(now, fund.subscription_cutoff, navUpdateDay);
        }
        if (fund.redemption_cutoff) {
            expected.redemption = getExpectedNavDateForNow(now, fund.redemption_cutoff, navUpdateDay);
        }
        return expected;
    }, [
        navUpdateDay,
        fund.subscription_cutoff?.daysBefore,
        fund.subscription_cutoff?.hour,
        fund.subscription_cutoff?.minute,
        fund.redemption_cutoff?.daysBefore,
        fund.redemption_cutoff?.hour,
        fund.redemption_cutoff?.minute,
    ]);

    const expectedNowDayMillisByType = React.useMemo(() => {
        const values: Partial<Record<SubscriptionType, number>> = {};
        ORDER_TYPES.forEach((type) => {
            const expectedDate = expectedNow?.[type];
            if (expectedDate) {
                values[type] = toStartOfDay(expectedDate).getTime();
            }
        });
        return values;
    }, [expectedNow?.subscription ? expectedNow.subscription.getTime() : null, expectedNow?.redemption ? expectedNow.redemption.getTime() : null]);

    const expectedNowMismatchByType = React.useMemo(() => {
        const values: Partial<Record<SubscriptionType, boolean>> = {};
        if (nextNavDateDayMillis === null) {
            return values;
        }
        ORDER_TYPES.forEach((type) => {
            const expectedDayMillis = expectedNowDayMillisByType[type];
            values[type] = typeof expectedDayMillis === "number" && expectedDayMillis !== nextNavDateDayMillis;
        });
        return values;
    }, [expectedNowDayMillisByType, nextNavDateDayMillis]);

    const expectedNowMismatchDaySet = React.useMemo(
        () =>
            new Set(
                ORDER_TYPES
                    .map((type) => (expectedNowMismatchByType[type] ? expectedNowDayMillisByType[type] : null))
                    .filter((value): value is number => typeof value === "number"),
            ),
        [expectedNowMismatchByType, expectedNowDayMillisByType],
    );

    const todayDayMillis = toStartOfDay(new Date()).getTime();

    const windowCalendarDefaultDate = React.useMemo(() => {
        if (waitingWindowStart && processingWindowStart) {
            return waitingWindowStart < processingWindowStart ? toStartOfDay(waitingWindowStart) : toStartOfDay(processingWindowStart);
        }
        return waitingWindowStart
            ? toStartOfDay(waitingWindowStart)
            : processingWindowStart
                ? toStartOfDay(processingWindowStart)
                : navDate
                    ? toStartOfDay(navDate)
                    : new Date();
    }, [waitingWindowStart, processingWindowStart, navDate ? navDate.getTime() : null]);

    const hasConfiguredWindows = Boolean(waitingWindowStart || processingWindowStart);
    const formatDateTime = (value: Date) => `${dateFormater(value)} ${value.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
    const formatWindowRange = (window?: OrderWindow) => window ? `${formatDateTime(window.start)} -> ${formatDateTime(window.end)}` : "N/A";
    const hasExpectedNow = Boolean(expectedNow?.subscription || expectedNow?.redemption);
    const hasExpectedMismatch = expectedNowMismatchDaySet.size > 0;
    const hasSameExpectedNow = Boolean(
        expectedNow?.subscription &&
        expectedNow?.redemption &&
        toStartOfDay(expectedNow.subscription).getTime() === toStartOfDay(expectedNow.redemption).getTime(),
    );

    const getDayProps = (value: string) => {
        const date = fromMantineDate(value)!;
        const dayMillis = toStartOfDay(date).getTime();
        const isNavDate = navDateDayMillis !== null && dayMillis === navDateDayMillis;
        const isNextNavDate = nextNavDateDayMillis !== null && dayMillis === nextNavDateDayMillis;
        const expectedNowMismatchTypes = ORDER_TYPES.filter(
            (type) => expectedNowMismatchByType[type] && expectedNowDayMillisByType[type] === dayMillis,
        );
        const isExpectedNowMismatch = expectedNowMismatchTypes.length > 0;
        const waitingEndTypes = ORDER_TYPES.filter((type) => {
            const window = waitingWindows[type];
            return window ? toStartOfDay(window.end).getTime() === dayMillis : false;
        });
        const isWaitingEndDay = waitingEndTypes.length > 0;
        const isToday = dayMillis === todayDayMillis;
        const waitingMatches = ORDER_TYPES.filter((type) => isDateInWindow(date, waitingWindows[type]));
        const processingMatches = ORDER_TYPES.filter((type) => isDateInWindow(date, processingWindows[type]));
        const inWaiting = waitingMatches.length > 0;
        const inProcessing = processingMatches.length > 0;
        const tooltipLines: string[] = [];

        if (isToday) {
            tooltipLines.push("Today");
        }
        if (isNavDate) {
            tooltipLines.push("NAV");
        }
        if (isNextNavDate) {
            tooltipLines.push(hasExpectedMismatch ? "Next NAV (mismatch)" : "Next NAV");
        }
        expectedNowMismatchTypes.forEach((type) => {
            tooltipLines.push(`Expected NAV now (${type})`);
        });
        waitingEndTypes.forEach((type) => {
            tooltipLines.push(`Waiting for NAV cutoff day (${type})`);
        });
        waitingMatches.forEach((type) => {
            tooltipLines.push(`Waiting for NAV (${type}): ${formatWindowRange(waitingWindows[type])}`);
        });
        processingMatches.forEach((type) => {
            tooltipLines.push(`Processing (${type}): ${formatWindowRange(processingWindows[type])}`);
        });
        const tooltip = tooltipLines.length ? tooltipLines.join("\n") : undefined;

        if (isNavDate) {
            return {
                title: tooltip,
                style: {
                    background: "var(--mantine-color-yellow-4)",
                    color: "var(--mantine-color-dark-9)",
                    border: "2px solid var(--mantine-color-yellow-8)",
                    boxShadow: isToday
                        ? "inset 0 0 0 1px var(--mantine-color-white), 0 0 0 2px var(--mantine-color-cyan-6)"
                        : "inset 0 0 0 1px var(--mantine-color-white)",
                },
            };
        }

        if (isNextNavDate) {
            return {
                title: tooltip,
                style: {
                    background: hasExpectedMismatch ? "var(--mantine-color-orange-4)" : "var(--mantine-color-lime-4)",
                    color: "var(--mantine-color-dark-9)",
                    border: hasExpectedMismatch ? "2px solid var(--mantine-color-orange-8)" : "2px solid var(--mantine-color-lime-8)",
                    boxShadow: isToday
                        ? "inset 0 0 0 1px var(--mantine-color-white), 0 0 0 2px var(--mantine-color-cyan-6)"
                        : "inset 0 0 0 1px var(--mantine-color-white)",
                },
            };
        }

        if (isExpectedNowMismatch) {
            return {
                title: tooltip,
                style: {
                    background: "var(--mantine-color-green-5)",
                    color: "var(--mantine-color-white)",
                    border: "2px solid var(--mantine-color-green-8)",
                    boxShadow: isToday
                        ? "inset 0 0 0 1px var(--mantine-color-white), 0 0 0 2px var(--mantine-color-cyan-6)"
                        : "inset 0 0 0 1px var(--mantine-color-white)",
                },
            };
        }

        if (!inWaiting && !inProcessing && !isToday) {
            return {};
        }

        if (!inWaiting && !inProcessing && isToday) {
            return {
                title: tooltip,
                style: {
                    border: "1px solid var(--mantine-color-cyan-5)",
                    boxShadow: "inset 0 0 0 1px var(--mantine-color-cyan-1)",
                    background: "var(--mantine-color-cyan-0)",
                },
            };
        }

        const waitingBackground = waitingCount > 0 ? "var(--mantine-color-blue-5)" : "var(--mantine-color-blue-2)";
        const waitingBorder = waitingCount > 0 ? "1px solid var(--mantine-color-blue-7)" : "1px solid var(--mantine-color-blue-4)";
        const waitingColor = waitingCount > 0 ? "var(--mantine-color-white)" : "var(--mantine-color-dark-8)";
        const processingBackground = processingCount > 0 ? "var(--mantine-color-gray-4)" : "var(--mantine-color-gray-2)";
        const processingBorder = processingCount > 0 ? "1px solid var(--mantine-color-gray-6)" : "1px solid var(--mantine-color-gray-4)";
        const background = inWaiting && inProcessing
            ? `linear-gradient(135deg, ${waitingBackground} 0%, ${waitingBackground} 50%, ${processingBackground} 50%, ${processingBackground} 100%)`
            : inWaiting
                ? isWaitingEndDay
                    ? `linear-gradient(135deg, ${waitingBackground} 0%, ${waitingBackground} 50%, var(--mantine-color-gray-0) 50%, var(--mantine-color-gray-0) 100%)`
                    : waitingBackground
                : processingBackground;

        return {
            title: tooltip,
            style: {
                background,
                color: inWaiting ? waitingColor : "var(--mantine-color-dark-8)",
                border: inWaiting ? waitingBorder : processingBorder,
                boxShadow: isToday ? "0 0 0 2px var(--mantine-color-cyan-6)" : undefined,
            },
        };
    };

    return (
        <Card withBorder radius="sm" p="sm">
            <Stack gap={6}>
                <Group justify="space-between" align="flex-start">
                    <Stack gap={0}>
                        <Text size="sm" fw={600}>Order windows calendar</Text>
                    </Stack>
                    <Group gap={6}>
                        <Badge variant="light" color="blue" size="sm">Current NAV Order Window</Badge>
                        <Badge variant="light" color="gray" size="sm">Processing</Badge>
                        <Badge variant="light" color="yellow" size="sm">NAV Date</Badge>
                        <Badge variant="light" color={hasExpectedMismatch ? "orange" : "lime"} size="sm">Next NAV</Badge>
                        {hasExpectedMismatch && (
                            <Badge variant="light" color="green" size="sm">Expected Now</Badge>
                        )}
                        <Badge variant="light" color="cyan" size="sm">Today</Badge>
                    </Group>
                </Group>

                <Calendar
                    key={`order-calendar-${windowCalendarDefaultDate.getFullYear()}-${windowCalendarDefaultDate.getMonth()}-${waitingCount}-${processingCount}`}
                    static
                    locale="en-gb"
                    firstDayOfWeek={0}
                    weekendDays={weekendDays}
                    defaultDate={windowCalendarDefaultDate}
                    numberOfColumns={2}
                    getDayProps={getDayProps}
                />

                {hasExpectedNow && (
                    <Paper withBorder radius="sm" p="xs" bg="var(--mantine-color-blue-0)">
                        <Stack gap={2}>
                            <Text size="xs" fw={600}>Orders Placed Now</Text>
                            {hasSameExpectedNow && expectedNow?.subscription ? (
                                <Text size="xs" c={hasExpectedMismatch ? "green" : undefined}>
                                    Expected NAV date: {dateFormater(expectedNow.subscription)}
                                </Text>
                            ) : (
                                <>
                                    {expectedNow?.subscription && (
                                        <Text size="xs" c={expectedNowMismatchByType.subscription ? "green" : undefined}>
                                            Subscription expected NAV: {dateFormater(expectedNow.subscription)}
                                        </Text>
                                    )}
                                    {expectedNow?.redemption && (
                                        <Text size="xs" c={expectedNowMismatchByType.redemption ? "green" : undefined}>
                                            Redemption expected NAV: {dateFormater(expectedNow.redemption)}
                                        </Text>
                                    )}
                                </>
                            )}
                        </Stack>
                    </Paper>
                )}

                {!hasConfiguredWindows && (
                    <Text size="xs" c="dimmed">
                        Configure subscription/redemption cutoff settings to highlight date ranges.
                    </Text>
                )}

                <Table withTableBorder withColumnBorders highlightOnHover>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>
                                <Text size="xs">Type</Text>
                            </Table.Th>
                            <Table.Th>
                                <Text size="xs">Current NAV Order Window</Text>
                            </Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {ORDER_TYPES.map((type) => (
                            <Table.Tr key={`window-row-${type}`}>
                                <Table.Td>
                                    <Text size="xs" tt="capitalize" fw={600}>{type}</Text>
                                </Table.Td>
                                <Table.Td>
                                    <Text size="xs" c="dimmed">{formatWindowRange(waitingWindows[type])}</Text>
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </Stack>
        </Card>
    );
};

export default OrderWindowsCalendar;
